/**
 * Die gemeinsame Grenze und die gemeinsamen Fehlertexte aller Handuploads (Task 4, "Uploads bis
 * 100 MB"). Jeder Uploadweg - Dokumente der Bestellung, Positionsdokumente, Produktionsbilder,
 * Belege der Zahlungen und der Kurssicherung, zentrale Dokumente einer Sammelsendung - prueft mit
 * `istZuGross` und meldet mit `dateiZuGrossText` bzw. `uploadFehlerText`. Eine zweite Zahl oder ein
 * zweiter Wortlaut waere die naechste Stelle, an der zwei Uploadwege verschieden urteilen.
 *
 * Gilt NUR fuer den Handupload. Die maschinellen Wege (DSV-Dokumente, SharePoint-Import, ...)
 * bleiben im Backend bei 15 MB (`MAX_DATEIGROESSE_BYTES`, `backend/src/documents/storage.ts`);
 * das Frontend kennt sie nicht.
 *
 * Die Texte sind unpersoenlich (kein "du"/"Sie") und stehen woertlich so in der Tabelle des Briefs
 * zu Task 4. Der Status entscheidet, nicht der Fehlercode des Backends: vor dem Backend kann Apache
 * mit einem 413 und einem HTML-Koerper antworten, der keinen `code` traegt.
 *
 * ZYKLUS MIT `api/basis.ts` (bewusst): dieses Modul liest dort `ApiFehler`, `basis.ts#ladeHoch`
 * wirft `UploadVerbindungsFehler` von hier. Beide benutzen die Klasse des anderen nur zur LAUFZEIT
 * eines Aufrufs, nie beim Auswerten des Moduls - die Reihenfolge der Imports spielt keine Rolle.
 */
import { ApiFehler } from "../api/basis.ts";

/** Handupload-Grenze, einschliesslich: 100 MiB, angezeigt als "100 MB". */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

/** `true`, wenn die Datei groesser als die Handupload-Grenze ist. Genau 100 MiB sind erlaubt. */
export function istZuGross(datei: { size: number }): boolean {
  return datei.size > MAX_UPLOAD_BYTES;
}

const EIN_MB = 1024 * 1024;

const MB_FORMAT = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Bytes als MB (1 MB = 1024 * 1024 Byte), deutsch, genau eine Nachkommastelle: `123,4 MB`. */
export function formatiereMb(bytes: number): string {
  return `${MB_FORMAT.format(bytes / EIN_MB)} MB`;
}

/**
 * Die Groesse fuer die Meldung "zu gross": wie `formatiereMb`, ausser knapp ueber der Grenze. Dort rundete die
 * Anzeige auf "100,0 MB" ab, und "ist zu groß (100,0 MB). Erlaubt sind höchstens 100 MB" las sich wie ein
 * Widerspruch (Zweig-Review M-3). Liegt die gerundete Anzeige nicht ueber der Grenze, die Datei aber schon, wird
 * stattdessen auf die naechste Stufe aufgerundet (104857601 Byte -> "100,1 MB"). Sonst bleibt es beim Runden:
 * ein reines Aufrunden machte aus 129394279 Byte (123,40000057 MB) "123,5 MB".
 */
function groesseFuerGrenzmeldung(bytes: number): string {
  const mb = bytes / EIN_MB;
  const grenzeMb = MAX_UPLOAD_BYTES / EIN_MB;
  const gerundet = Math.round(mb * 10) / 10;
  const angezeigt = bytes > MAX_UPLOAD_BYTES && gerundet <= grenzeMb ? Math.ceil(mb * 10) / 10 : mb;
  return `${MB_FORMAT.format(angezeigt)} MB`;
}

/** Der Text, wenn eine Datei die Grenze reisst - vorab im Browser wie auch nach einem 413. */
export function dateiZuGrossText(datei: { name: string; size: number }): string {
  return `${datei.name} ist zu groß (${groesseFuerGrenzmeldung(datei.size)}). Erlaubt sind höchstens 100 MB je Datei.`;
}

/**
 * Der Upload kam gar nicht erst bis zu einer HTTP-Antwort: Netzfehler, abgebrochene Verbindung
 * oder `status === 0`. Wirft `api/basis.ts#ladeHoch`.
 */
export class UploadVerbindungsFehler extends Error {
  constructor(meldung = "Der Upload wurde unterbrochen, weil die Verbindung abgebrochen ist.") {
    super(meldung);
    this.name = "UploadVerbindungsFehler";
  }
}

/**
 * Die Datei IST gespeichert, nur das Nachladen danach scheiterte (Fix-Runde 1 zu Task 5). Wer einen Upload
 * samt Nachladen in einer Zusage zusammenfasst (`components/App/useDrawerDokumente.ts`), lehnt in diesem Fall
 * mit dieser Klasse ab, damit `uploadFehlerText` NICHT "konnte nicht gespeichert werden" sagt - der Nutzer
 * luede die Datei sonst ein zweites Mal hoch. `cause` traegt den Fehler des Nachladens.
 */
export class NachladenNachUploadFehler extends Error {
  constructor(meldung: string, optionen?: { cause?: unknown }) {
    super(meldung, optionen);
    this.name = "NachladenNachUploadFehler";
  }
}

/**
 * Wer und was hinter einem stehenden Hinweis zu einem Positionsdokument-Upload steht. Wird beim START
 * des Uploads festgehalten, nicht beim Scheitern gelesen: bis ein Upload von 100 MB scheitert, zeigt
 * dieselbe Komponente womoeglich eine andere Bestellung.
 */
export interface HinweisKontext {
  /** Bezeichnung des Dokumenttyps, z. B. "Artwork" oder "Produktionsbilder". */
  label: string;
  bestellNummer: string;
  artikelNummer: string | null;
}

/**
 * Der Text eines stehenden Hinweises zu einem gescheiterten Positionsdokument-Upload:
 * `Artwork für BE-10440 (301020): <text>`. Die Klammer entfaellt ohne Artikelnummer. Der eine Wortlaut
 * fuer das Positionen-Grid (`PositionsdokumentAktionen.tsx`) und den Reiter Dokumente
 * (`Positionsdokumente.tsx`); `text` kommt aus `uploadFehlerText`.
 */
export function stehenderHinweisText(kontext: HinweisKontext, text: string): string {
  const artikel = kontext.artikelNummer ? ` (${kontext.artikelNummer})` : "";
  return `${kontext.label} für ${kontext.bestellNummer}${artikel}: ${text}`;
}

/**
 * Die Meldung, wenn ein Upload eine Position erreicht, die inzwischen durch einen anderen Upload belegt ist (Upload-
 * Sperren, Spec 2026-10-20): das Positionen-Grid (`PositionsdokumentAktionen.tsx`) und der Abschnitt Positionsdokumente
 * im Reiter Dokumente (`Positionsdokumente.tsx`) teilen sich die Sperre je Position - und diesen einen Wortlaut. Die
 * Eingaben und "Ersatz hochladen" sind bei belegter Position `disabled`; kommt ein Ereignis trotzdem an (die Sperre
 * trifft zwischen dem letzten Zeichnen und dem Ereignis ein), soll es nicht still ins Leere laufen.
 */
export const POSITION_BELEGT_TEXT = "Für diese Position läuft gerade ein Upload. Bitte warten, bis er beendet ist.";

/** Der Text des Servers, wenn der Koerper ein JSON mit nicht leerem String `error` ist, sonst `null`. */
function serverText(koerper: unknown): string | null {
  if (typeof koerper !== "object" || koerper === null) return null;
  const fehler = (koerper as { error?: unknown }).error;
  return typeof fehler === "string" && fehler !== "" ? fehler : null;
}

/** Der Text zu einem gescheiterten Upload von `datei` - der eine Wortlaut fuer alle Uploadwege. */
export function uploadFehlerText(fehler: unknown, datei: { name: string; size: number }): string {
  // ZUERST: die Datei liegt auf dem Server, egal was die Ursache des gescheiterten Nachladens war (auch ein 413
  // oder ein Verbindungsabbruch beim Nachladen sagt nichts ueber den Upload).
  if (fehler instanceof NachladenNachUploadFehler) {
    return `${datei.name} ist gespeichert, die Anzeige ist aber nicht mehr aktuell. Bitte neu laden.`;
  }
  if (fehler instanceof ApiFehler && fehler.status === 413) return dateiZuGrossText(datei);
  if (fehler instanceof UploadVerbindungsFehler) {
    return `Der Upload von ${datei.name} wurde unterbrochen, weil die Verbindung abgebrochen ist. Bitte erneut versuchen.`;
  }
  if (fehler instanceof ApiFehler) {
    if (fehler.status === 408 || fehler.status === 504) {
      return `Der Upload von ${datei.name} hat zu lange gedauert. Bitte erneut versuchen.`;
    }
    if (fehler.status === 403) return "Für diesen Dokumenttyp fehlt die Berechtigung zum Hochladen.";
    if (fehler.status >= 400 && fehler.status < 500) {
      const text = serverText(fehler.koerper);
      if (text !== null) return text;
    }
    return `${datei.name} konnte nicht gespeichert werden (Fehler ${fehler.status}). Bitte erneut versuchen.`;
  }
  return `${datei.name} konnte nicht hochgeladen werden. Bitte erneut versuchen.`;
}
